"use client";

import { useState } from "react";
import {
  ServerActionResult,
  UseServerActionOptions,
} from "modules/common/components/toast/types";
import { useToast } from "modules/common/components/toast/useToast";
import { getUserFriendlyMessage } from "utils/error-sanitizer";

/**
 * Hook for working with server actions that trigger toast notifications
 */
export function useServerAction<TResult, TArgs extends any[]>(
  action: (...args: TArgs) => Promise<ServerActionResult<TResult>>,
  options?: UseServerActionOptions<TResult>
) {
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<TResult | null>(null);
  const toast = useToast();

  const execute = async (
    ...args: TArgs
  ): Promise<ServerActionResult<TResult>> => {
    setIsPending(true);
    setError(null);

    // Clear any lingering toasts first
    // toast.dismiss();

    // Create a loading toast if needed
    let loadingToast;
    if (options?.loadingMessage) {
      loadingToast = toast.loading({
        description: options.loadingMessage,
      });
    }

    try {
      const result = await action(...args);

      if (result.success) {
        setData(result.data as TResult);

        // Dismiss loading toast if it exists
        if (loadingToast) {
          loadingToast.dismiss();
        }

        // Show success toast if needed (with a delay to ensure loading toast is gone)
        if (options?.showSuccessToast !== false && result.message) {
          toast.success({ description: result.message });
        }

        options?.onSuccess?.(result.data as TResult);
        return result;
      } else {
        setError(result.error || "An error occurred");

        // Dismiss loading toast if it exists
        if (loadingToast) {
          loadingToast.dismiss();
        }

        // Show error toast if needed (with a delay to ensure loading toast is gone)
        if (options?.showErrorToast !== false && result.error) {
          toast.error({
            description: result.humanError
              ? result.error
              : getUserFriendlyMessage(result.error),
          });
        }

        options?.onError?.(result.error || "An error occurred");
        return result;
      }
    } catch (e) {
      const errorMessage =
        e instanceof Error ? e.message : "An unexpected error occurred";
      setError(errorMessage);

      // Dismiss loading toast if it exists
      if (loadingToast) {
        loadingToast.dismiss();
      }

      // Show error toast if needed (with a delay to ensure loading toast is gone)
      if (options?.showErrorToast !== false) {
        toast.error(getUserFriendlyMessage(e));
      }

      options?.onError?.(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsPending(false);
    }
  };

  return {
    execute,
    isPending,
    error,
    data,
    reset: () => {
      setData(null);
      setError(null);
    },
  };
}
